import React, {useState} from 'react';
import {View} from 'react-native';
import moment from 'moment';

import sy from '~/styles';
import {
  Text,
  Checkbox,
  Pressable,
  Dialog,
  ServiceWarning,
} from '~/components/controls';
import {service_request as serviceRequestApi} from '~/api/private';
import {SERVICE_TYPE_COMPONENTS, SERVICE_TYPE_CODES} from '~/types/services';
import {showTecrmiDialog} from '~/actions';
import {useDispatch} from '~/lib/hooks';
import {
  TIRE_PERMISSION_LABELS,
  TIRE_PERMISSION_SORT,
  ROB_TASK_STATUS,
} from '~/types/rob';

import CarRepairIcon from '~/images/md-icons/car_repair/materialicons/24px.svg';
import ApprovalIcon from '~/images/md-icons/approval/materialicons/24px.svg';
import TireRepairIcon from '~/images/md-icons/tire_repair/materialicons/24px.svg';
import InfoOutlinedIcon from '~/images/md-icons/info/materialsymbolsoutlined/24px.svg';
import PropTypes from 'prop-types';

const maybeEnableTecrmi = async ({
  dispatch,
  request_id,
  is_retail_car,
  type,
  tecrmi_enabled,
  onRefresh,
}) => {
  if (
    !is_retail_car &&
    type === SERVICE_TYPE_CODES.Maintenance &&
    !tecrmi_enabled > 0
  ) {
    await new Promise((resolve, reject) => {
      dispatch(
        showTecrmiDialog({
          resolve,
          reject,
        }),
      );
    });

    await serviceRequestApi.enable_tecrmi(request_id);
    await onRefresh();
  }
};

const getMaintenanceInfo = ({disabled, contract, mileage, car}) => {
  if (disabled) {
    return {
      subtitle: 'Niet in contract',
      icon: <CarRepairIcon fill="#828282" />,
    };
  }

  let text = 'Laatst niet bekend';
  let last_service_date;

  if (
    contract?.last_service_request?.maintenance ||
    contract?.vehicle?.maintenance?.lastMaintenanceDate
  ) {
    let date;
    let code;

    const last_bk_maintenance_date = contract?.last_service_request?.maintenance
      ?.date
      ? moment(contract?.last_service_request?.maintenance?.date)
      : null;
    const last_rob_maintenance_date = contract?.vehicle?.maintenance
      ?.lastMaintenanceDate
      ? moment(contract?.vehicle?.maintenance?.lastMaintenanceDate)
      : null;

    if (
      !last_rob_maintenance_date ||
      last_bk_maintenance_date?.isSameOrAfter(last_rob_maintenance_date, 'date')
    ) {
      date = last_bk_maintenance_date;
      code = contract?.last_service_request?.maintenance?.code;
    } else {
      date = last_rob_maintenance_date;
      code = contract.leaseContract
        ? `(${contract.vehicle.maintenance.lastMaintenanceCode})`
        : '';
    }

    last_service_date = date;
    text = `Laatst bekend ${moment(date).format('DD-MM-YYYY')} ${
      code ? `(${code})` : ''
    }`;
  }

  return {
    subtitle: (
      <View style={[sy['flex-row'], sy['items-center'], sy['gap-2']]}>
        {Boolean(text) && (
          <Text style={[sy.small, sy['text-lightgray']]}>{text}</Text>
        )}
        <ServiceWarning
          service={{
            last: last_service_date,
            kilometrage: mileage,
            ascription: car?.entry_date,
          }}
        />
      </View>
    ),
    icon: <CarRepairIcon fill="#828282" />,
  };
};

const ROB_INSPECTION_WITH_MAINTENANCE_CODE = '1001';
const ROB_INSPECTION_CODE = '1002';

const isRobComponentCancelled = (rob_component) =>
  [ROB_TASK_STATUS.Cancelled, ROB_TASK_STATUS.CancellationApproved].includes(
    rob_component?.status.code,
  );

const findRequestComponent = (components, type) =>
  components.find(
    (item) => item.component_name === SERVICE_TYPE_COMPONENTS[type],
  );

const isInspectionRequired = ({components, rob}) => {
  // A maintenance task that isn't in the rob request yet is not cancelled.
  const maintenance_rob_components = (
    findRequestComponent(components, SERVICE_TYPE_CODES.Maintenance)?.tasks ??
    []
  ).map((task) => rob?.components?.[task.id]);

  const rob_request_components = Object.values(rob?.components ?? {});

  const isRobCodeCancelled = (code) =>
    rob_request_components.some(
      (item) => item.rob.code === code && isRobComponentCancelled(item),
    );

  if (
    maintenance_rob_components.length > 0 &&
    !maintenance_rob_components.every(isRobComponentCancelled)
  ) {
    // With maintenance the APK can only be added as 1001, which is already in
    // the request, just cancelled.
    return isRobCodeCancelled(ROB_INSPECTION_WITH_MAINTENANCE_CODE);
  }

  // Without maintenance the APK can only be added as 1002, which is already in
  // the request, just cancelled.
  return isRobCodeCancelled(ROB_INSPECTION_CODE);
};

const getInspectionInfo = ({disabled, contract, datetime, components, rob}) => {
  if (disabled) {
    return {
      subtitle: 'Niet in contract',
      icon: <ApprovalIcon fill="#828282" />,
      hasPriority: false,
      hasInspectionInfo: false,
    };
  }

  const nextPeriodicCheck = contract?.vehicle?.maintenance?.nextPeriodicCheck;
  const subtitle = nextPeriodicCheck
    ? `Vervalt ${moment(nextPeriodicCheck).format('DD-MM-YYYY')}`
    : null;
  const hasPriority =
    nextPeriodicCheck &&
    moment(datetime).add(2, 'months').isAfter(moment(nextPeriodicCheck));
  const hasInspectionInfo =
    !hasPriority &&
    contract?.vehicle?.maintenance?.nextPeriodicCheckExtended &&
    moment().isAfter(
      moment(contract?.vehicle?.maintenance?.nextPeriodicCheckExtended),
      'date',
    );

  const required = isInspectionRequired({components, rob});

  return {
    subtitle,
    icon: <ApprovalIcon fill="#828282" />,
    hasPriority,
    hasInspectionInfo,
    ...(required && {disabled: true, checked: true}),
  };
};

const getTirePermissions = ({is_retail_car, contract}) =>
  Object.keys(
    is_retail_car
      ? {swap: true, replace: true, repair: true}
      : contract?.permissions.tires ?? {},
  )
    .filter((item) => TIRE_PERMISSION_LABELS[item])
    .sort(
      (a, b) =>
        TIRE_PERMISSION_SORT.indexOf(a) - TIRE_PERMISSION_SORT.indexOf(b),
    );

const getTiresInfo = ({is_retail_car, contract}) => {
  const allowed = [];
  const denied = [];

  getTirePermissions({is_retail_car, contract}).forEach((item) => {
    if (is_retail_car || contract.permissions.tires[item]) {
      allowed.push(TIRE_PERMISSION_LABELS[item]);
    } else {
      denied.push(TIRE_PERMISSION_LABELS[item]);
    }
  });

  let subtitle;
  if (denied.length === 0) {
    subtitle = allowed.join(', ');
  } else if (allowed.length === 0) {
    subtitle = `${denied.join(', ')} niet in contract`;
  } else {
    subtitle = `${allowed.join(', ')} (${denied.join(', ')} niet in contract)`;
  }

  return {
    disabled: allowed.length === 0,
    subtitle: subtitle.charAt(0).toUpperCase() + subtitle.slice(1),
    icon: <TireRepairIcon fill="#828282" />,
  };
};

const getServiceMeta = ({
  type,
  is_retail_car,
  contract,
  mileage,
  car,
  datetime,
  components,
  rob,
}) => {
  const disabled =
    !is_retail_car && contract?.permissions.repairAndMaintenance !== true;
  let hasPriority = false;
  let hasInspectionInfo = false;
  let serviceInfo = {
    disabled,
    checked: false,
    subtitle: null,
    icon: null,
  };

  switch (type) {
    case SERVICE_TYPE_CODES.Maintenance: {
      serviceInfo = {
        ...serviceInfo,
        ...getMaintenanceInfo({disabled, contract, mileage, car}),
      };
      break;
    }

    case SERVICE_TYPE_CODES.Inspection: {
      const inspectionInfo = getInspectionInfo({
        disabled,
        contract,
        datetime,
        components,
        rob,
      });
      hasPriority = inspectionInfo.hasPriority;
      hasInspectionInfo = inspectionInfo.hasInspectionInfo;
      serviceInfo = {
        ...serviceInfo,
        ...inspectionInfo,
      };
      break;
    }

    case SERVICE_TYPE_CODES.Tires: {
      serviceInfo = {
        ...serviceInfo,
        ...getTiresInfo({is_retail_car, contract}),
      };
      break;
    }

    default:
      break;
  }

  return {
    disabled: serviceInfo.disabled,
    checked: serviceInfo.checked,
    subtitle: serviceInfo.subtitle,
    icon: serviceInfo.icon,
    hasPriority,
    hasInspectionInfo,
  };
};

const getInspectionPriorityInfo = (contract) => ({
  title: 'APK vervallen',
  description: `APK vervallen op ${moment(
    contract?.vehicle?.maintenance?.nextPeriodicCheck,
  ).format('D MMMM YYYY')}`,
});

const getInspectionCombinedInfo = (contract) => ({
  title: 'APK combineren met onderhoud',
  description: `Verzoek LM: APK combineren met onderhoud bij vervaldatum binnen ${moment(
    contract?.vehicle.maintenance.nextPeriodicCheck,
  ).diff(
    moment(contract?.vehicle.maintenance.nextPeriodicCheckExtended),
    'months',
  )} maanden.`,
});

const ServiceComponent = ({
  request_id,
  type,
  datetime,
  is_retail_car,
  contract,
  car,
  mileage,
  tecrmi_enabled,
  components,
  rob,
  checked,
  onCheck,
  onRefresh,
}) => {
  const dispatch = useDispatch();
  const [showInspectionInfoDialog, setShowInspectionInfoDialog] =
    useState(false);

  let {
    disabled,
    checked: required,
    subtitle,
    icon,
    hasPriority,
    hasInspectionInfo,
  } = getServiceMeta({
    type,
    is_retail_car,
    contract,
    mileage,
    car,
    datetime,
    components,
    rob,
  });

  if (!disabled) {
    const request_component = findRequestComponent(components, type);

    if (request_component) {
      // If it's checked than disable the possibility to delete this component.
      disabled = true;
    }
  }

  const onToggle = async () => {
    try {
      await maybeEnableTecrmi({
        dispatch,
        request_id,
        is_retail_car,
        type,
        tecrmi_enabled,
        onRefresh,
      });
    } catch {
      return;
    }

    onCheck(SERVICE_TYPE_COMPONENTS[type], type);
  };

  const onShowPriorityInfo = () => {
    setShowInspectionInfoDialog(getInspectionPriorityInfo(contract));
  };

  const onShowCombinedInfo = () => {
    setShowInspectionInfoDialog(getInspectionCombinedInfo(contract));
  };

  return (
    <View style={[sy['flex-row'], sy['p-4'], sy['gap-4']]}>
      <Pressable disabled={disabled} onPress={onToggle}>
        <Checkbox
          style={[sy['justify-start']]}
          checked={checked || required}
          disabled={disabled}
          disabledTimeout={0}
        />
      </Pressable>
      <View style={[sy['flex-row'], sy['flex-1'], sy['justify-between']]}>
        <View>
          <Text>{SERVICE_TYPE_COMPONENTS[type]}</Text>
          <View style={[sy['flex-row'], sy['gap-2']]}>
            <Text style={[sy.small, sy['text-lightgray']]}>{subtitle}</Text>
            {hasPriority && (
              <Pressable onPress={onShowPriorityInfo}>
                <InfoOutlinedIcon fill="#FF6C00" width={18} height={18} />
              </Pressable>
            )}
            {hasInspectionInfo && (
              <Pressable onPress={onShowCombinedInfo}>
                <InfoOutlinedIcon fill="#FF6C00" width={18} height={18} />
              </Pressable>
            )}
          </View>
        </View>
        {icon}
      </View>

      <Dialog
        visible={!!showInspectionInfoDialog}
        title={showInspectionInfoDialog?.title}
        titleIcon={<InfoOutlinedIcon fill="#FF6C00" />}
        onDismiss={() => setShowInspectionInfoDialog(null)}>
        <View style={[sy['flex-row'], sy['py-4']]}>
          <Text>{showInspectionInfoDialog?.description}</Text>
        </View>
      </Dialog>
    </View>
  );
};
ServiceComponent.propTypes = {
  request_id: PropTypes.any,
  type: PropTypes.any,
  datetime: PropTypes.any,
  is_retail_car: PropTypes.any,
  contract: PropTypes.any,
  car: PropTypes.any,
  mileage: PropTypes.any,
  tecrmi_enabled: PropTypes.any,
  components: PropTypes.any,
  rob: PropTypes.any,
  checked: PropTypes.any,
  onCheck: PropTypes.any,
  onRefresh: PropTypes.any,
};

export default ServiceComponent;
